import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  ArrowRight,
  Sparkle,
  Buildings,
  Rocket,
} from "@phosphor-icons/react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";
import { createCheckoutSession } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Pricing page — 4 tiers + monthly/annual toggle. The feature matrix mirrors
// the backend `tiers.py` source of truth (caps + features per plan). Edit
// both if you change anything.
const TIERS = [
  {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    tagline: "For the curious + the cautious.",
    tone: "violet",
    features: [
      "Up to 10 projects",
      "5 collaborators per project",
      "All four date columns",
      "Subtasks + dependencies",
      "Risk heatmap (read-only)",
      "PDF/PNG/CSV/XLSX export",
    ],
    ctaLabel: "Start free",
    requiresAuth: false,
  },
  {
    id: "starter",
    name: "Starter",
    monthly: 9,
    annual: 90,
    tagline: "For solo PMs who outgrow the free tier.",
    tone: "amber",
    features: [
      "Up to 25 projects",
      "15 collaborators per project",
      "Full risk heatmap",
      "Critical path computation",
      "Project transfer + ownership",
      "Saved views & version history",
    ],
    ctaLabel: "Choose Starter",
    requiresAuth: true,
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 19,
    annual: 190,
    tagline: "For project teams that actually run shipments.",
    tone: "indigo",
    featured: true,
    features: [
      "Unlimited projects",
      "25 collaborators per project",
      "Everything in Starter",
      "Custom columns (text/int/enum/dep)",
      "Priority email support",
      "Unlimited saved views + history",
    ],
    ctaLabel: "Go Pro",
    requiresAuth: true,
  },
  {
    id: "team",
    name: "Team",
    monthly: 34,
    annual: 340,
    tagline: "For PMOs running 12 things at once.",
    tone: "cyan",
    features: [
      "Unlimited projects + collaborators",
      "Everything in Pro",
      "Role-based access control",
      "Audit log + activity feed",
      "Per-user notification settings",
      "SSO (coming soon)",
    ],
    ctaLabel: "Start Team",
    requiresAuth: true,
  },
];

export default function Pricing() {
  // Annual is the default — biggest savings, hides the smaller monthly number
  // first. Toggle persists during the session so users browsing back/forth
  // don't get bounced between cadences.
  const [annual, setAnnual] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const onSelectTier = async (tier) => {
    if (tier.id === "free") {
      navigate(user ? "/dashboard" : "/register");
      return;
    }
    if (!user) {
      // Send the user to register, remember their pick so we can resume after.
      sessionStorage.setItem("tg_pending_checkout", JSON.stringify({
        tier: tier.id,
        cadence: annual ? "annual" : "monthly",
      }));
      navigate(`/register?next=/pricing`);
      return;
    }
    try {
      const { url } = await createCheckoutSession({
        tier: tier.id,
        cadence: annual ? "annual" : "monthly",
        origin_url: window.location.origin,
      });
      if (url) window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    }
  };

  return (
    <div className="min-h-screen tg-public-bg">
      <PublicNav />

      {/* HERO */}
      <section className="mx-auto max-w-[1600px] px-4 pt-16 pb-12 md:pt-24">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 mb-6 tg-shadow-soft">
            <Sparkle size={11} weight="fill" className="text-indigo-500" />
            Plans that scale with your standup
          </span>
          <h1 className="text-5xl md:text-7xl tracking-tight leading-[1.05] text-slate-950">
            Invest in your <span className="tg-gradient-text">next launch.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            We're big fans of you and keeping this thing running, but even the
            best charts in life aren't entirely free — shocking, we know. Pay
            annually and get <span className="font-semibold text-slate-950">2 months free</span>.
          </p>

          {/* Cadence toggle */}
          <div className="mt-8 flex items-center justify-center" data-testid="cadence-toggle">
            <div className="inline-flex p-1 rounded-full bg-white border border-slate-200 tg-shadow-soft">
              <button
                type="button"
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-colors ${
                  !annual ? "bg-slate-950 text-white" : "text-slate-700"
                }`}
                data-testid="cadence-monthly"
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-colors flex items-center gap-2 ${
                  annual ? "bg-slate-950 text-white" : "text-slate-700"
                }`}
                data-testid="cadence-annual"
              >
                Annual
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  annual ? "bg-emerald-400/90 text-emerald-950" : "bg-emerald-100 text-emerald-700"
                }`}>
                  Save ~17%
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING CARDS */}
      <section className="mx-auto max-w-[1600px] px-4 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {TIERS.map((t) => (
            <Tier key={t.id} tier={t} annual={annual} onSelect={() => onSelectTier(t)} />
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white border-y border-slate-200 py-20" data-testid="why-choose-section">
        <div className="mx-auto max-w-[1600px] px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-3">
              Why bother
            </div>
            <h2 className="text-4xl md:text-5xl text-slate-950 mb-5">
              Why choose <span className="tg-gradient-text">ThatGantt?</span>
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The proof is in the people. We're passionate about delivering not
              just a great UX but a qualitatively different way to plan. Click
              any row below for the deeply unnecessary backstory.
            </p>
          </div>
          <div className="space-y-3">
            <FAQRow title="Responsive support — from a human" copy="Dedicated to providing prompt, courteous customer service. Reply in 1–3 business days. No tier-2 escalation. No 'ticket has been moved to L3.' Just emails from a person." />
            <FAQRow title="Delivery of promise" copy="If a feature is in the docs, it ships. If it's not, we won't fake a demo. (Looking at you, every other 2024 PM tool launch.)" />
            <FAQRow title="We listen to feedback" copy="Most of this app exists because someone griped on a support ticket and we agreed. Email us; if you're reasonable, you'll see it shipped within the quarter." />
          </div>
        </div>
      </section>

      {/* ENTERPRISE */}
      <section className="py-24">
        <div className="mx-auto max-w-[1600px] px-4">
          <div className="rounded-3xl tg-gradient p-10 md:p-16 text-white tg-shadow-soft grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <Buildings size={32} weight="fill" className="text-white mb-4" />
              <h2 className="text-3xl md:text-4xl mb-3 text-white">
                Need something bigger?
              </h2>
              <p className="text-white/80 leading-relaxed">
                We do custom contracts for enterprise teams running serious
                portfolios. SSO, SCIM, audit retention, the whole "we have a
                CISO" package. Email us — we'll quote it in plain English.
              </p>
            </div>
            <div className="flex flex-col md:items-end gap-3">
              <Link to="/contact?topic=enterprise">
                <Button
                  className="h-12 px-7 rounded-full bg-white text-indigo-700 hover:bg-slate-100 font-medium text-base gap-2 border-0"
                  data-testid="pricing-enterprise-cta"
                >
                  Talk to us
                  <ArrowRight size={16} weight="bold" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
      <HaveAQuestionBadge />
    </div>
  );
}

// ---------- tier card ----------

function Tier({ tier, annual, onSelect }) {
  const { id, name, monthly, annual: annualPrice, tagline, tone, featured, features, ctaLabel } = tier;
  const toneMap = {
    violet: "bg-violet-100 text-violet-700",
    amber: "bg-amber-100 text-amber-700",
    indigo: "tg-gradient text-white",
    cyan: "bg-cyan-100 text-cyan-700",
  };
  const ringMap = {
    indigo: "ring-2 ring-indigo-500/40 tg-shadow-soft",
  };
  // Display "$X/mo" for monthly, "$Y/yr" for annual. Free renders as $0.
  const displayPrice = id === "free" ? "$0" : annual ? `$${annualPrice}` : `$${monthly}`;
  const displayCadence = id === "free" ? "/forever" : annual ? "/year" : "/month";
  // Equivalent monthly for annual — frames the discount.
  const equivalentMonthly = annual && monthly > 0 ? `$${(annualPrice / 12).toFixed(2)}/mo equivalent` : null;

  return (
    <div
      className={`relative rounded-3xl bg-white border border-slate-200 p-8 flex flex-col ${featured ? ringMap.indigo : ""}`}
      data-testid={`pricing-tier-${id}`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest tg-gradient text-white">
            <Rocket size={10} weight="fill" />
            Most popular
          </span>
        </div>
      )}

      <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${toneMap[tone]} mb-5`}>
        <Sparkle size={18} weight="fill" />
      </div>

      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">{name}</div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-5xl font-display font-bold text-slate-950" data-testid={`pricing-${id}-price`}>{displayPrice}</span>
        <span className="text-sm text-slate-500">{displayCadence}</span>
      </div>
      {equivalentMonthly && (
        <div className="text-[11px] text-slate-500 mb-2 font-mono">{equivalentMonthly}</div>
      )}
      <p className="text-sm text-slate-600 mb-6 mt-2">{tagline}</p>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <CheckCircle size={14} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onSelect}
        className={`w-full h-12 rounded-full font-medium text-sm border-0 ${
          featured
            ? "tg-gradient text-white hover:opacity-95"
            : "bg-slate-950 text-white hover:bg-slate-800"
        }`}
        data-testid={`pricing-cta-${id}`}
      >
        {ctaLabel}
      </Button>
    </div>
  );
}

function FAQRow({ title, copy }) {
  return (
    <details className="group rounded-2xl bg-slate-50 border border-slate-200 p-5 transition-colors hover:bg-white">
      <summary className="flex items-center justify-between cursor-pointer list-none">
        <span className="text-base font-semibold text-slate-950">{title}</span>
        <ArrowRight size={14} weight="bold" className="text-slate-400 group-open:rotate-90 transition-transform" />
      </summary>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{copy}</p>
    </details>
  );
}
